/**
 * Arm-space (ADR-017) — the tier-1 rotate-at-play character property: one
 * constant abduction offset for the two upper arms, derived entirely from the
 * rig's own declared conventions. Pure: RigDescriptor + degrees in, per-bone
 * parent-bind-frame pre-rotation quaternions out. The display layer LEFT-
 * multiplies them onto a clip's arm quaternion tracks; a constant unit
 * quaternion is a linear map on quaternion components, so it commutes with
 * normalized-lerp track interpolation — offsetting the keys IS offsetting
 * every sampled frame. Kernel ClipData is never touched: exports and the bake
 * are immune by construction (ADR-017 §2).
 *
 * Frame choice: the axis is the declared world `facing`, transported into
 * each arm's PARENT bind frame — pre-multiplying the child local rotates the
 * arm about the character's forward axis as carried by the live torso (bend
 * the spine, the abduction plane bends with it). The upper arm's own local
 * axes are deliberately not consulted: the clavicle class is where the
 * ontology declares childDirectionInformative: false (ADR-003 §11).
 *
 * Depends on: math/vec3+quat, ir/rig-descriptor, ir/errors.
 * Depended on by: the app's clip display filter — never retarget, never export.
 */
import { KernelError } from '../ir/errors.js';
import type { RigDescriptor, RigBone } from '../ir/rig-descriptor.js';
import {
  quatFromAxisAngle,
  quatInvertUnit,
  quatMultiply,
  quatNormalize,
  quatRotateVec3,
  type Quat,
} from '../math/quat.js';
import { vec3Cross, vec3Dot, vec3Sub, type Vec3Data } from '../math/vec3.js';
import { readPoqpoqExtra, withPoqpoqExtra } from './poqpoq-extras.js';

/**
 * The one canonical band (ADR-017 §7): the Document schema gate and this
 * derivation refuse the same range, imported from the same constant — two
 * doors, one value (consumer-sweep rule).
 */
export const ARM_SPACE_MAX_DEGREES = 45;

/** The ontology ids arm-space addresses — the upper arms, both or neither. */
export const ARM_SPACE_ONTOLOGY_IDS = ['LeftArm', 'RightArm'] as const;

/**
 * A rig this feature cannot carry says WHY (ADR-017 §7): the panel renders
 * `reason` as its disabled state, so deliberate zero and broken are visibly
 * different. Never a partial pair — one resolvable arm is a refusal.
 */
export class ArmSpaceRefusal extends KernelError {
  constructor(readonly reason: string) {
    super(`arm-space: ${reason}`);
  }
}

export interface ArmSpaceOffset {
  /** The rig's own bone name (the track target the display clip carries). */
  readonly bone: string;
  /** Constant parent-bind-frame pre-rotation: q_display = rotation ∘ q_track. */
  readonly rotation: Quat;
}

/**
 * A rotation the display applies about a WORLD axis, conjugated per key
 * through the PLAYED parent frame (the 2026-08-03 amendment). The bind-frame
 * transport in {@link ArmSpaceOffset} assumed the played parent stays near
 * bind — measured false: under absolute transport a VRM's played shoulder
 * sits a large frame-delta from its identity bind, which flipped the
 * abduction's visible sign ("wider made narrower"), and even on Tripo the
 * transported axis landed mostly parallel to the hanging arm (two thirds of
 * the offset was invisible twist). The plan form carries the INTENT — bone,
 * world axis, signed angle — and the display door owns the frame math at
 * play time, where the true parent orientation is known.
 */
export interface WorldAxisRotation {
  readonly bone: string;
  /** Unit world axis (the declared triad's own vectors). */
  readonly axis: Vec3Data;
  readonly radians: number;
  /**
   * What the display door does when the clip carries NO track for `bone`.
   * 'synthesize' (the default; arm-space, #77): the offset is applied over
   * the bone's rest local, so a slider always moves the bone. 'skip' (the
   * head-seating preview, ADR-030 / Codex P1 on #126): a SEATING is a
   * bind-relative fact that shows only when the bone is DRIVEN — an undriven
   * bone rests at its bind, where the flesh reads the authored head — so a
   * preview that synthesized motion there would move what the commit does
   * not, and snap back on release.
   */
  readonly untracked?: 'synthesize' | 'skip';
}

const FACING_VECTORS: Record<RigDescriptor['facing'], Vec3Data> = {
  '+X': { x: 1, y: 0, z: 0 },
  '-X': { x: -1, y: 0, z: 0 },
  '+Z': { x: 0, y: 0, z: 1 },
  '-Z': { x: 0, y: 0, z: -1 },
};
const UP_VECTORS: Record<RigDescriptor['upAxis'], Vec3Data> = {
  Y: { x: 0, y: 1, z: 0 },
  Z: { x: 0, y: 0, z: 1 },
};

/**
 * The rig's declared world axes as vectors — the ONE reading of the declared
 * triad every character property derives from (arm-space here, head-space one
 * chain over). Degenerate up/facing combos are refused at the rig gate, so
 * `up × facing` is unit-length by construction for every value this returns.
 */
export function declaredAxes(rig: Pick<RigDescriptor, 'facing' | 'upAxis'>): {
  readonly facing: Vec3Data;
  readonly up: Vec3Data;
} {
  return { facing: FACING_VECTORS[rig.facing], up: UP_VECTORS[rig.upAxis] };
}

function resolveArm(rig: RigDescriptor, ontologyId: string): { bone: RigBone; parent: RigBone } {
  const entries = rig.mapping.filter((m) => m.ontologyId === ontologyId);
  if (entries.length === 0) {
    throw new ArmSpaceRefusal(`this rig maps no bone to "${ontologyId}" — arm-space needs both upper arms`);
  }
  if (entries.length > 1) {
    throw new ArmSpaceRefusal(
      `"${ontologyId}" is ambiguous on this rig (${entries.map((e) => e.rigBone).join(', ')}) — refusing to pick`,
    );
  }
  const name = (entries[0] as (typeof entries)[number]).rigBone;
  const bone = rig.bones.find((b) => b.name === name);
  if (bone === undefined) {
    throw new ArmSpaceRefusal(`mapping names "${name}" for "${ontologyId}" but the rig carries no such bone`);
  }
  if (bone.parent === null) {
    throw new ArmSpaceRefusal(`"${name}" has no parent — no frame to carry the abduction plane`);
  }
  const parent = rig.bones.find((b) => b.name === bone.parent);
  if (parent === undefined) {
    throw new ArmSpaceRefusal(`"${name}"'s parent "${bone.parent}" is not in the rig`);
  }
  return { bone, parent };
}

/**
 * Derive the two constant offsets, or refuse naming why. Zero degrees returns
 * [] — the documented "no filter" state, distinct from a refusal.
 *
 * Sign is MEASURED, never assumed from a naming convention: rotation about
 * +facing is counterclockwise in the (lateral, up) plane by construction of
 * the right-handed triad (lateral = up × facing), so abduction — the arm
 * sweeping away from the torso through its own side — is +θ for the arm on
 * the +lateral side of the arms' midpoint and −θ for the other, whatever the
 * rest pose (T, A, or hanging). Arms not laterally separated name no sides
 * and refuse.
 */
export function armSpaceOffsets(rig: RigDescriptor, degrees: number): readonly ArmSpaceOffset[] {
  if (!Number.isFinite(degrees) || Math.abs(degrees) > ARM_SPACE_MAX_DEGREES) {
    throw new ArmSpaceRefusal(
      `${degrees}° is outside the ±${ARM_SPACE_MAX_DEGREES}° band — refused, never clamped silently`,
    );
  }
  const [left, right] = ARM_SPACE_ONTOLOGY_IDS.map((id) => resolveArm(rig, id)) as [
    { bone: RigBone; parent: RigBone },
    { bone: RigBone; parent: RigBone },
  ];
  if (degrees === 0) return [];

  const { facing, up } = declaredAxes(rig);
  const lateral = vec3Cross(up, facing); // unit: up ⟂ facing (degenerate combos refused at the rig gate)

  const pL = left.bone.worldBind.position;
  const pR = right.bone.worldBind.position;
  const mid = { x: (pL.x + pR.x) / 2, y: (pL.y + pR.y) / 2, z: (pL.z + pR.z) / 2 };
  const radians = (degrees * Math.PI) / 180;

  return [left, right].map(({ bone, parent }) => {
    const side = vec3Dot(vec3Sub(bone.worldBind.position, mid), lateral);
    if (side === 0) {
      throw new ArmSpaceRefusal(
        `"${bone.name}" sits on the midline — the arms are not laterally separated, no abduction side exists`,
      );
    }
    // World facing, expressed in the parent's bind frame: pre-multiplying the
    // child local with a rotation about this axis rotates the arm about the
    // character's forward axis as carried by the LIVE parent (torso-relative).
    const parentRotation = quatNormalize(parent.worldBind.rotation);
    const axisInParent = quatRotateVec3(quatInvertUnit(parentRotation), facing);
    return { bone: bone.name, rotation: quatFromAxisAngle(axisInParent, radians * Math.sign(side)) };
  });
}

/**
 * The plan form of {@link armSpaceOffsets}: same gates, same measured sides,
 * same signs — but the output names the WORLD facing axis and a signed angle
 * per arm, for the played-frame conjugation door (withWorldAxisPreRotation).
 * Zero degrees returns [] — the documented "no filter" state.
 */
export function armSpacePlan(rig: RigDescriptor, degrees: number): readonly WorldAxisRotation[] {
  if (!Number.isFinite(degrees) || Math.abs(degrees) > ARM_SPACE_MAX_DEGREES) {
    throw new ArmSpaceRefusal(
      `${degrees}° is outside the ±${ARM_SPACE_MAX_DEGREES}° band — refused, never clamped silently`,
    );
  }
  const [left, right] = ARM_SPACE_ONTOLOGY_IDS.map((id) => resolveArm(rig, id)) as [
    { bone: RigBone; parent: RigBone },
    { bone: RigBone; parent: RigBone },
  ];
  if (degrees === 0) return [];

  const { facing, up } = declaredAxes(rig);
  const lateral = vec3Cross(up, facing);
  const pL = left.bone.worldBind.position;
  const pR = right.bone.worldBind.position;
  const mid = { x: (pL.x + pR.x) / 2, y: (pL.y + pR.y) / 2, z: (pL.z + pR.z) / 2 };
  const radians = (degrees * Math.PI) / 180;

  return [left, right].map(({ bone }) => {
    const side = vec3Dot(vec3Sub(bone.worldBind.position, mid), lateral);
    if (side === 0) {
      throw new ArmSpaceRefusal(
        `"${bone.name}" sits on the midline — the arms are not laterally separated, no abduction side exists`,
      );
    }
    return { bone: bone.name, axis: facing, radians: radians * Math.sign(side) };
  });
}

/** The display composition, named so tests and the adapter share one door. */
export function applyArmSpaceOffset(offset: Quat, track: Quat): Quat {
  return quatMultiply(offset, track);
}

// ---------------------------------------------------------------------------
// THE ARM-SPACE DECLARATION (ADR-030 §13, ruled 2026-08-28: arm-space is a
// torso-axis abduction AT PLAY — the display filter is the definition, the arm
// rest is never touched). A play-time property cannot ride the bind, so the
// exported file DECLARES it in glTF `extras` on each upper-arm node, and a
// reader (World; this app on reopen) applies it every frame: rotate that
// bone's WORLD orientation by `degrees` about the unit world `axis` (the
// character's facing), position unchanged — equivalently, pre-multiply the
// local by parentWorld⁻¹ · R · parentWorld, the played-frame conjugation this
// app's filter performs. A consumer that ignores it plays the authored rest
// and the unmodified clips: neutral, and correct. `character` is the value
// the person set (the slider); `degrees` is this bone's signed share (left +,
// right −, from armSpacePlan) so a reader needs no notion of side or family.
// ---------------------------------------------------------------------------

/** The key under `extras.poqpoq` that carries a bone's arm-space share. */
export const ARM_SPACE_MARKER_KEY = 'armSpace';

export interface ArmSpaceMarker {
  /** The character's arm-space as set (the slider), degrees, band ±ARM_SPACE_MAX_DEGREES. */
  readonly character: number;
  /** THIS bone's signed rotation about `axis`, degrees (left +, right − for a positive character value). */
  readonly degrees: number;
  /** Unit world axis — the character's declared facing. */
  readonly axis: Vec3Data;
}

/** Read a bone's arm-space declaration; absent → null; malformed → refused naming where. */
export function parseArmSpaceMarker(userData: unknown, where: string): ArmSpaceMarker | null {
  const refuse = (why: string): never => {
    throw new ArmSpaceRefusal(`${where} carries a malformed arm-space marker (extras.poqpoq.${ARM_SPACE_MARKER_KEY}): ${why}`);
  };
  const raw = readPoqpoqExtra(userData, ARM_SPACE_MARKER_KEY);
  if (raw === null) return refuse('"poqpoq" is not an object');
  if (raw === undefined) return null;
  if (typeof raw !== 'object') return refuse('not an object');
  const { character, degrees, axis } = raw as Record<string, unknown>;
  if (typeof character !== 'number' || !Number.isFinite(character)) return refuse(`character ${String(character)} is not a finite number`);
  if (Math.abs(character) > ARM_SPACE_MAX_DEGREES) return refuse(`character ${character} is outside the ±${ARM_SPACE_MAX_DEGREES}° band`);
  if (typeof degrees !== 'number' || !Number.isFinite(degrees)) return refuse(`degrees ${String(degrees)} is not a finite number`);
  if (Math.abs(degrees) > ARM_SPACE_MAX_DEGREES) return refuse(`degrees ${degrees} is outside the ±${ARM_SPACE_MAX_DEGREES}° band`);
  if (axis === null || typeof axis !== 'object') return refuse('axis is not an object');
  const a = axis as Record<string, unknown>;
  const x = a['x'];
  const y = a['y'];
  const z = a['z'];
  if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number' || !Number.isFinite(x + y + z)) return refuse('axis is not {x, y, z}');
  const len = Math.hypot(x, y, z);
  if (!(Math.abs(len - 1) < 1e-3)) return refuse(`axis has length ${len.toFixed(4)}, not 1`);
  return { character, degrees, axis: { x, y, z } };
}

/** A node's userData with the arm-space declaration written (or, for null, withdrawn) — every other key preserved. */
export function withArmSpaceMarker(userData: unknown, marker: ArmSpaceMarker | null): Record<string, unknown> {
  return withPoqpoqExtra(
    userData,
    ARM_SPACE_MARKER_KEY,
    marker === null
      ? undefined
      : { character: marker.character, degrees: marker.degrees, axis: { x: marker.axis.x, y: marker.axis.y, z: marker.axis.z } },
  );
}
